import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Package as PackageIcon, Plus, Pencil, Trash2, X, Check, Euro, Users, Truck,
  Sparkles, Layers,
} from "lucide-react";
import ModuleHint from "@/components/ModuleHint";

export default function SuperAdminPackages() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [flagsRegistry, setFlagsRegistry] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [pkgs, mods] = await Promise.all([
        api.get("/packages"),
        api.get("/packages/modules"),
      ]);
      setPackages(pkgs.data);
      setCatalog(mods.data);
      // Feature flag registry (for the default_feature_flags section) —
      // fetch from any tenant's feature-flags endpoint since registry is global
      if (pkgs.data.length > 0 || true) {
        try {
          const tenants = await api.get("/tenants");
          if (tenants.data.length > 0) {
            const r = await api.get(`/tenants/${tenants.data[0].id}/feature-flags`);
            setFlagsRegistry(r.data.registry || []);
          }
        } catch (err) { console.warn("feature-flags registry preload failed:", err); /* optional — UI works without it */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function removePackage(p) {
    if (p.tenant_count > 0) {
      toast.error(`Paket wird von ${p.tenant_count} Mandant(en) verwendet — erst Mandanten umziehen`);
      return;
    }
    if (!window.confirm(`Paket "${p.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/packages/${p.id}`);
      toast.success("Paket gelöscht");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  }

  async function applyToAll(p) {
    if (!window.confirm(`Paket "${p.name}" auf alle ${p.tenant_count} Mandanten anwenden?\n\nFreigegebene Module werden aktualisiert, bestehende Feature-Flag-Overrides bleiben erhalten.`)) return;
    try {
      const { data } = await api.post(`/packages/${p.id}/apply`);
      toast.success(`${data.tenants_affected} Mandant(en) aktualisiert`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anwenden fehlgeschlagen");
    }
  }

  // Hook-safe gate
  if (user && !user.is_super_admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="p-6 md:p-8" data-testid="super-admin-packages-page">
      <ModuleHint moduleKey="packages" />
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold text-zinc-100 inline-flex items-center gap-2">
            <PackageIcon className="w-6 h-6 text-amber-400"/>
            Abo-Pakete
            <span className="ml-2 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">Super-Admin</span>
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Definiere, welche Module, Feature-Flags und Limits jeder Tarif enthält.
          </p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          data-testid="pkg-new-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded">
          <Plus className="w-4 h-4"/> Neues Paket
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-zinc-500 text-sm">Laden…</div>
      ) : packages.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded p-10 text-center text-zinc-500 text-sm">
          <PackageIcon className="w-10 h-10 mx-auto mb-3 text-zinc-700"/>
          Noch keine Pakete angelegt.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.map(p => (
            <PackageCard key={p.id} pkg={p} catalog={catalog}
              onEdit={() => { setEditing(p); setShowForm(true); }}
              onDelete={() => removePackage(p)}
              onApply={() => applyToAll(p)}
            />
          ))}
        </div>
      )}

      {showForm && (
        <PackageForm
          editing={editing}
          catalog={catalog}
          flagsRegistry={flagsRegistry}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function PackageCard({ pkg, catalog, onEdit, onDelete, onApply }) {
  const enabled = new Set(pkg.enabled_modules || []);
  const accent = pkg.name === "Enterprise" ? "purple" : pkg.name === "Pro" ? "blue" : "zinc";
  const accentCls = {
    purple: "border-purple-500/30 bg-purple-500/5",
    blue: "border-blue-500/30 bg-blue-500/5",
    zinc: "border-zinc-800 bg-zinc-900",
  }[accent];
  return (
    <div data-testid={`pkg-card-${pkg.name.toLowerCase()}`}
      className={`border rounded-lg p-5 ${accentCls}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-display text-xl font-semibold text-zinc-50">{pkg.name}</h3>
          <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{pkg.description}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-display font-semibold text-zinc-50">€{pkg.price_monthly_eur}</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">pro Monat</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <MetricPill icon={Layers} value={(pkg.enabled_modules || []).length} label="Module"/>
        <MetricPill icon={Users} value={pkg.max_users || "∞"} label="Nutzer"/>
        <MetricPill icon={Truck} value={pkg.max_vehicles || "∞"} label="Fahrzeuge"/>
      </div>

      <div className="space-y-1 mb-4 max-h-36 overflow-y-auto">
        {catalog.map(m => {
          const on = enabled.has(m.key);
          return (
            <div key={m.key} className={`flex items-center gap-2 text-xs ${on ? "text-zinc-200" : "text-zinc-600"}`}>
              {on ? <Check className="w-3 h-3 text-emerald-400 shrink-0"/> : <X className="w-3 h-3 text-zinc-700 shrink-0"/>}
              <span className="truncate">{m.label}</span>
              {m.always_on && <span className="text-[9px] text-zinc-600 uppercase">inkl.</span>}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
        <div className="text-[10px] text-zinc-500">
          {pkg.tenant_count || 0} {(pkg.tenant_count || 0) === 1 ? "Mandant" : "Mandanten"}
        </div>
        <div className="inline-flex items-center gap-2">
          {(pkg.tenant_count || 0) > 0 && (
            <button onClick={onApply} data-testid={`pkg-apply-${pkg.name.toLowerCase()}`}
              title="Auf alle zugeordneten Mandanten anwenden"
              className="inline-flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300">
              <Sparkles className="w-3.5 h-3.5"/> Anwenden
            </button>
          )}
          <button onClick={onEdit} data-testid={`pkg-edit-${pkg.name.toLowerCase()}`}
            title="Bearbeiten"
            className="text-zinc-400 hover:text-zinc-100">
            <Pencil className="w-3.5 h-3.5"/>
          </button>
          <button onClick={onDelete} data-testid={`pkg-delete-${pkg.name.toLowerCase()}`}
            title="Löschen"
            className="text-zinc-500 hover:text-red-400 disabled:opacity-40"
            disabled={(pkg.tenant_count || 0) > 0}>
            <Trash2 className="w-3.5 h-3.5"/>
          </button>
        </div>
      </div>
    </div>
  );
}

function MetricPill({ icon: Icon, value, label }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-center">
      <Icon className="w-3 h-3 text-zinc-500 mx-auto mb-0.5"/>
      <div className="text-xs font-semibold text-zinc-200">{value}</div>
      <div className="text-[9px] text-zinc-600 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function PackageForm({ editing, catalog, flagsRegistry, onClose, onSaved }) {
  const isEdit = !!editing;
  const alwaysOn = new Set(catalog.filter(m => m.always_on).map(m => m.key));
  const [form, setForm] = useState(() => editing ? {
    name: editing.name, description: editing.description || "",
    price_monthly_eur: editing.price_monthly_eur || 0,
    price_yearly_eur: editing.price_yearly_eur || 0,
    trial_days: editing.trial_days || 14,
    enabled_modules: editing.enabled_modules || [],
    default_feature_flags: editing.default_feature_flags || {},
    max_users: editing.max_users || 0,
    max_vehicles: editing.max_vehicles || 0,
    is_active: editing.is_active !== false,
    sort_order: editing.sort_order || 100,
  } : {
    name: "", description: "",
    price_monthly_eur: 0, price_yearly_eur: 0, trial_days: 14,
    enabled_modules: [...alwaysOn],
    default_feature_flags: {},
    max_users: 0, max_vehicles: 0,
    is_active: true, sort_order: 100,
  });
  const [saving, setSaving] = useState(false);

  function toggleModule(key) {
    if (alwaysOn.has(key)) return;
    setForm(f => {
      const s = new Set(f.enabled_modules);
      if (s.has(key)) s.delete(key); else s.add(key);
      return { ...f, enabled_modules: [...s] };
    });
  }

  function toggleFlag(key) {
    setForm(f => {
      const nf = { ...f.default_feature_flags };
      if (nf[key]) delete nf[key]; else nf[key] = true;
      return { ...f, default_feature_flags: nf };
    });
  }

  async function save() {
    if (!form.name?.trim()) { toast.error("Name pflicht"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/packages/${editing.id}`, form);
        toast.success("Paket aktualisiert");
      } else {
        await api.post("/packages", form);
        toast.success("Paket angelegt");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="pkg-form-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-3xl max-h-[94vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <h3 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
            <PackageIcon className="w-4 h-4 text-blue-400"/>
            {isEdit ? `Paket "${editing.name}" bearbeiten` : "Neues Paket anlegen"}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="pkg-form-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Basic, Pro, Enterprise, Fleet"
                data-testid="pkg-name"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Aktiv</label>
              <select value={form.is_active ? "1" : "0"}
                onChange={(e) => setForm({ ...form, is_active: e.target.value === "1" })}
                data-testid="pkg-active"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                <option value="1">Ja (sichtbar & buchbar)</option>
                <option value="0">Nein (archiviert)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase text-zinc-500">Beschreibung</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="Was macht dieses Paket besonders?"
              data-testid="pkg-description"
              className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
          </div>

          {/* Pricing */}
          <div className="bg-zinc-950 border border-zinc-800 rounded p-4">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2 inline-flex items-center gap-1">
              <Euro className="w-3 h-3"/> Preisgestaltung
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] text-zinc-500">Monatlich €</label>
                <input type="number" min={0} step={0.01} value={form.price_monthly_eur}
                  onChange={(e) => setForm({ ...form, price_monthly_eur: +e.target.value })}
                  data-testid="pkg-price-monthly"
                  className="w-full mt-0.5 px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-sm text-zinc-100"/>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500">Jährlich €</label>
                <input type="number" min={0} step={0.01} value={form.price_yearly_eur}
                  onChange={(e) => setForm({ ...form, price_yearly_eur: +e.target.value })}
                  data-testid="pkg-price-yearly"
                  className="w-full mt-0.5 px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-sm text-zinc-100"/>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500">Trial-Tage</label>
                <input type="number" min={0} max={90} value={form.trial_days}
                  onChange={(e) => setForm({ ...form, trial_days: +e.target.value })}
                  data-testid="pkg-trial"
                  className="w-full mt-0.5 px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-sm text-zinc-100"/>
              </div>
            </div>
          </div>

          {/* Limits */}
          <div className="bg-zinc-950 border border-zinc-800 rounded p-4">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">Nutzungs-Limits (0 = unbegrenzt)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-zinc-500">Max. Nutzer</label>
                <input type="number" min={0} value={form.max_users}
                  onChange={(e) => setForm({ ...form, max_users: +e.target.value })}
                  data-testid="pkg-max-users"
                  className="w-full mt-0.5 px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-sm text-zinc-100"/>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500">Max. Fahrzeuge</label>
                <input type="number" min={0} value={form.max_vehicles}
                  onChange={(e) => setForm({ ...form, max_vehicles: +e.target.value })}
                  data-testid="pkg-max-vehicles"
                  className="w-full mt-0.5 px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-sm text-zinc-100"/>
              </div>
            </div>
          </div>

          {/* Modules */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-2">Freigegebene Module</label>
            <div className="grid grid-cols-2 gap-2">
              {catalog.map(m => {
                const on = form.enabled_modules.includes(m.key);
                const forced = alwaysOn.has(m.key);
                return (
                  <button key={m.key} onClick={() => toggleModule(m.key)}
                    disabled={forced}
                    data-testid={`pkg-mod-${m.key}`}
                    className={`p-2.5 rounded border text-left text-xs transition-colors ${
                      on
                        ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-200"
                        : "bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-zinc-700"
                    } ${forced ? "opacity-60 cursor-not-allowed" : ""}`}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="font-medium text-zinc-100">{m.label}</span>
                      {forced && <span className="text-[9px] uppercase text-zinc-500">fest</span>}
                      {on && !forced && <Check className="w-3.5 h-3.5 text-emerald-400"/>}
                    </div>
                    <div className="text-[10px] text-zinc-500 leading-tight">{m.description}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Feature flag defaults */}
          {flagsRegistry.length > 0 && (
            <div>
              <label className="text-[10px] uppercase text-zinc-500 block mb-2">Standard-Feature-Flags</label>
              <div className="bg-zinc-950 border border-zinc-800 rounded p-3 space-y-2 max-h-48 overflow-y-auto">
                {flagsRegistry.map(f => {
                  const on = !!form.default_feature_flags[f.key];
                  return (
                    <label key={f.key} className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={on} onChange={() => toggleFlag(f.key)}
                        data-testid={`pkg-flag-${f.key}`}
                        className="mt-0.5 accent-blue-500"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-zinc-200">{f.title}</div>
                        <div className="text-[10px] text-zinc-500">{f.description}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                Diese Flags sind für neue Mandanten dieses Pakets standardmäßig aktiv. Tenant-Overrides können sie später abschalten.
              </div>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-zinc-800 flex justify-end gap-2 sticky bottom-0 bg-zinc-900">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
          <button onClick={save} disabled={saving} data-testid="pkg-save-btn"
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 text-white rounded font-medium">
            {saving ? "Speichert…" : (isEdit ? "Speichern" : "Paket anlegen")}
          </button>
        </div>
      </div>
    </div>
  );
}
